import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, X, Star, StarOff, Trash2, Plus, DollarSign, TrendingUp, Eye, Download, MousePointerClick } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, authHeaders, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("vendors");
  const [stats, setStats] = useState({});
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [pricing, setPricing] = useState({ monthly_price: 99, featured_price: 149, featured_annual_price: 1788 });
  const [newPrice, setNewPrice] = useState("");
  const [newFeaturedPrice, setNewFeaturedPrice] = useState("");
  const [analytics, setAnalytics] = useState(null);

  const load = () => {
    Promise.all([
      axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      axios.get(`${API}/admin/vendors`, { headers: authHeaders() }),
      axios.get(`${API}/categories`),
      axios.get(`${API}/config/pricing`),
      axios.get(`${API}/admin/analytics`, { headers: authHeaders() }),
    ]).then(([s, v, c, p, a]) => {
      setStats(s.data); setVendors(v.data); setCategories(c.data); setPricing(p.data);
      setNewPrice(p.data.monthly_price);
      setNewFeaturedPrice(p.data.featured_price || 99);
      setAnalytics(a.data);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!authLoading && user?.role === "admin") load();
    // eslint-disable-next-line
  }, [authLoading, user]);

  if (authLoading) return null;
  if (!user || user.role !== "admin") {
    return <div className="px-6 py-24 text-center text-slate-400">Admin access only. <a href="/login" className="text-orange-500">Login</a></div>;
  }

  const updateVendor = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/vendors/${id}`, patch, { headers: authHeaders() });
      toast.success("Updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API}/admin/categories`, { name: newCat.trim() }, { headers: authHeaders() });
      setNewCat(""); load(); toast.success("Category added");
    } catch { toast.error("Failed"); }
  };
  const delCategory = async (id) => {
    try { await axios.delete(`${API}/admin/categories/${id}`, { headers: authHeaders() }); load(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };
  const updatePrice = async () => {
    try {
      await axios.post(
        `${API}/admin/pricing`,
        { monthly_price: parseFloat(newPrice), featured_price: parseFloat(newFeaturedPrice) },
        { headers: authHeaders() }
      );
      load(); toast.success("Pricing updated");
    } catch { toast.error("Failed"); }
  };

  const tabs = [
    { id: "vendors", label: "Vendors" },
    { id: "analytics", label: "Analytics" },
    { id: "categories", label: "Categories" },
    { id: "pricing", label: "Pricing" },
  ];

  return (
    <div className="px-6 sm:px-12 lg:px-24 py-12">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">// Admin Control Room</div>
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl text-slate-50 mb-8">Administration</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Total Vendors", value: stats.total ?? 0 },
          { label: "Approved", value: stats.approved ?? 0 },
          { label: "Active Subs", value: stats.active ?? 0 },
          { label: "Pending", value: stats.pending ?? 0 },
        ].map((s) => (
          <div key={s.label} className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-bold mb-2">{s.label}</div>
            <div className="font-display font-black text-3xl text-slate-50">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-px bg-slate-800 mb-6 rounded-sm overflow-hidden w-fit">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-6 py-3 text-xs font-bold uppercase tracking-wider ${tab === t.id ? "bg-orange-600 text-white" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "vendors" && (
        <div className="bg-slate-900 border border-slate-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <th className="p-4">Company</th>
                <th className="p-4">Owner</th>
                <th className="p-4">Sub</th>
                <th className="p-4">Approved</th>
                <th className="p-4">Featured</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                  <td className="p-4">
                    <div className="font-semibold text-slate-100">{v.company_name}</div>
                    <div className="text-xs text-slate-500">{v.city}{v.state ? `, ${v.state}` : ""}</div>
                  </td>
                  <td className="p-4 text-slate-400 text-xs">{v.owner_email}</td>
                  <td className="p-4">
                    {v.subscription_active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-500 font-bold uppercase tracking-wider"><Check className="w-3 h-3" />Active</span>
                    ) : (
                      <span className="text-xs text-slate-500 uppercase tracking-wider">None</span>
                    )}
                  </td>
                  <td className="p-4">
                    <button
                      data-testid={`admin-approve-${v.id}`}
                      onClick={() => updateVendor(v.id, { approved: !v.approved })}
                      className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm ${v.approved ? "bg-emerald-600/20 text-emerald-400" : "bg-slate-800 text-slate-300 border border-slate-700 hover:border-orange-600"}`}
                    >
                      {v.approved ? "Approved" : "Pending"}
                    </button>
                  </td>
                  <td className="p-4">
                    <button
                      data-testid={`admin-feature-${v.id}`}
                      onClick={() => updateVendor(v.id, { featured: !v.featured })}
                      className="text-slate-400 hover:text-orange-500"
                    >
                      {v.featured ? <Star className="w-5 h-5 fill-orange-500 text-orange-500" /> : <StarOff className="w-5 h-5" />}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <a href={`/vendor/${v.id}`} target="_blank" rel="noreferrer" className="text-xs text-orange-500 uppercase tracking-wider font-bold">View →</a>
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No vendors yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "analytics" && analytics && (
        <div className="space-y-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-4">// Revenue</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-slate-900 border-2 border-orange-600 p-6 rounded-sm" data-testid="analytics-mrr">
                <TrendingUp className="w-5 h-5 text-orange-500 mb-3" />
                <div className="font-display font-black text-3xl text-slate-50">${analytics.mrr.toLocaleString()}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-400 mt-1 font-bold">Monthly Recurring</div>
              </div>
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
                <DollarSign className="w-5 h-5 text-emerald-500 mb-3" />
                <div className="font-display font-black text-3xl text-slate-50">${analytics.annual_run_rate.toLocaleString()}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">Annual Run Rate</div>
              </div>
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm" data-testid="analytics-basic-count">
                <div className="text-xs uppercase tracking-[0.15em] text-orange-600 font-bold mb-2">Basic Members</div>
                <div className="font-display font-black text-3xl text-slate-50">{analytics.basic_count}</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">× ${analytics.basic_price}/mo</div>
              </div>
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm" data-testid="analytics-featured-count">
                <div className="text-xs uppercase tracking-[0.15em] text-amber-500 font-bold mb-2">Featured Members</div>
                <div className="font-display font-black text-3xl text-slate-50">{analytics.featured_count}</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">× ${analytics.featured_price}/mo</div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-4">// Engagement · Last 30 Days</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
                <Eye className="w-5 h-5 text-orange-500 mb-3" />
                <div className="font-display font-black text-3xl text-slate-50">{analytics.events_30d.profile_view.toLocaleString()}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">Profile Views</div>
              </div>
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
                <Download className="w-5 h-5 text-orange-500 mb-3" />
                <div className="font-display font-black text-3xl text-slate-50">{analytics.events_30d.linecard_download.toLocaleString()}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">Line Card Downloads</div>
              </div>
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
                <MousePointerClick className="w-5 h-5 text-orange-500 mb-3" />
                <div className="font-display font-black text-3xl text-slate-50">{analytics.events_30d.contact_click.toLocaleString()}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">Contact Clicks</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "categories" && (
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
          <div className="flex gap-2 mb-6">
            <input value={newCat} data-testid="admin-cat-input" onChange={(e) => setNewCat(e.target.value)} placeholder="New category name..." className="flex-1 px-3 py-2.5 bg-slate-950 border border-slate-700 text-slate-50 focus:border-orange-500 outline-none rounded-sm" />
            <button onClick={addCategory} data-testid="admin-cat-add" className="px-5 bg-orange-600 text-white font-bold uppercase tracking-wider rounded-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 text-slate-200 border border-slate-700 text-xs font-bold uppercase tracking-wider">
                {c.name}
                <button onClick={() => delCategory(c.id)} data-testid={`admin-cat-del-${c.id}`} className="text-slate-500 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      {tab === "pricing" && (
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm max-w-md space-y-6">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-2">Alliance Member (Basic)</h3>
            <div className="text-sm text-slate-400 mb-3">Current: <span className="text-slate-100 font-mono">${pricing.monthly_price}/mo</span></div>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input data-testid="admin-price-input" type="number" min="1" step="0.01" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} className="w-full pl-9 pr-3 py-2.5 bg-slate-950 border border-slate-700 text-slate-50 focus:border-orange-500 outline-none rounded-sm" />
            </div>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500 mb-2">Featured Vendor</h3>
            <div className="text-sm text-slate-400 mb-3">Current: <span className="text-slate-100 font-mono">${pricing.featured_price}/mo</span></div>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input data-testid="admin-featured-price-input" type="number" min="1" step="0.01" value={newFeaturedPrice} onChange={(e) => setNewFeaturedPrice(e.target.value)} className="w-full pl-9 pr-3 py-2.5 bg-slate-950 border border-slate-700 text-slate-50 focus:border-amber-500 outline-none rounded-sm" />
            </div>
          </div>
          <button onClick={updatePrice} data-testid="admin-price-save" className="w-full py-3 bg-orange-600 text-white font-bold uppercase tracking-wider rounded-sm">Save Pricing</button>
        </div>
      )}
    </div>
  );
}
